/**
 * PublicSurvey - Component for taking surveys via public link (no authentication)
 * 
 * Supports:
 * - Anonymous survey completion
 * - Multiple pages with questions
 * - Progress tracking
 * - All question types including custom answers
 */

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  LinearProgress,
  RadioGroup,
  Radio,
  FormControlLabel,
  Checkbox,
  TextField,
  Alert,
  FormGroup,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
} from '@mui/material';
import { CloudUpload as UploadIcon } from '@mui/icons-material';
import axios from 'axios';
import { getShuffledOptions, getShuffledImageOptions } from '../../utils/shuffleOptions';
import { matrixBlocksProgress, matrixProgressMessage, getMissingMatrixRows } from '../../utils/matrixCompletion';
import MatrixQuestion from '../../components/MatrixQuestion';
import {
  selectionHint,
  selectionProgressMessage,
  multiSelectBlocksProgress,
  isSelectionCapped,
} from '../../utils/multiSelectLimit';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export default function PublicSurvey() {
  const { id } = useParams(); // This can be either ID or customSlug
  // Личный код интервьюера (?i=...) — необязательный; без него опрос работает как раньше
  const interviewerCode = new URLSearchParams(window.location.search).get('i') || undefined;
  
  const [survey, setSurvey] = useState(null);
  const [surveyId, setSurveyId] = useState(null); // Store actual survey ID
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Page navigation
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [pageHistory, setPageHistory] = useState([0]);
  
  // Answers
  const [answers, setAnswers] = useState({});
  const [files, setFiles] = useState({}); // File uploads
  
  // Submission
  const [submitting, setSubmitting] = useState(false);
  const [startTime] = useState(() => Date.now());
  const [completed, setCompleted] = useState(false);
  const [disqualified, setDisqualified] = useState(false);
  const [disqualificationReason, setDisqualificationReason] = useState('');
  
  // Validation
  const [attemptedNext, setAttemptedNext] = useState(false);

  // Дополнительные вопросы интервьюера: отдельная последняя страница, только по ссылке
  // интервьюера (сервер отдаёт interviewerQuestions только при валидном коде)
  const [onExtraPage, setOnExtraPage] = useState(false);
  const [interviewerAnswers, setInterviewerAnswers] = useState({});
  const [attemptedExtra, setAttemptedExtra] = useState(false);
  const extraQuestions = (interviewerCode && survey && Array.isArray(survey.interviewerQuestions))
    ? survey.interviewerQuestions : [];
  const hasExtra = extraQuestions.length > 0;

  const isExtraUnanswered = (q) => {
    const v = interviewerAnswers[q.id];
    if (v === undefined || v === null) return true;
    if (Array.isArray(v)) return v.length === 0;
    return String(v).trim() === '';
  };
  const isExtraPageComplete = () => extraQuestions.every((q) => !q.required || !isExtraUnanswered(q));
  
  useEffect(() => {
    fetchSurvey();
  }, [id]);
  
  const fetchSurvey = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/surveys/public/${id}`, interviewerCode ? { params: { i: interviewerCode } } : undefined);
      
      const surveyData = response.data;
      
      // Store actual survey ID for later use
      setSurveyId(surveyData.id);
      
      // Ensure pages exist
      if (!surveyData.pages || surveyData.pages.length === 0) {
        surveyData.pages = [{
          id: 'page-1',
          title: 'Страница 1',
          questionIds: (surveyData.questions || []).map(q => q.id)
        }];
      }
      
      setSurvey(surveyData);
      setError('');
    } catch (err) {
      console.error('Error fetching public survey:', err);
      setError(err.response?.data?.error || 'Ошибка загрузки опроса');
    } finally {
      setLoading(false);
    }
  };
  
  const getCurrentPage = () => {
    if (!survey || !survey.pages) return null;
    return survey.pages[currentPageIndex] || null;
  };
  
  const getCurrentPageQuestions = () => {
    const page = getCurrentPage();
    if (!page || !survey || !survey.questions) return [];
    return survey.questions.filter(q => page.questionIds.includes(q.id));
  };
  
  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };
  
  const handleFileUpload = (questionId, file) => {
    if (!file) return;
    
    const maxSize = 15 * 1024 * 1024; // 15MB in bytes
    if (file.size > maxSize) {
      alert('Размер файла не должен превышать 15МБ');
      return;
    }
    
    setFiles(prev => ({ ...prev, [questionId]: file }));
  };
  
  // Check if current page is complete (required questions answered)
  const isCurrentPageComplete = () => {
    const pageQuestions = getCurrentPageQuestions();
    for (const q of pageQuestions) {
      if (multiSelectBlocksProgress(q, answers[q.id])) return false;
      if (matrixBlocksProgress(q, answers[q.id])) return false;
      if (q.required && isQuestionUnanswered(q.id)) {
        return false;
      }
    }
    return true;
  };
  
  const isQuestionUnanswered = (questionId) => {
    const answer = answers[questionId];
    const file = files[questionId];
    const question = survey.questions.find(q => q.id === questionId);
    
    // For file questions, check if file is uploaded
    if (question && question.type === 'file') {
      return !file;
    }
    
    if (answer === undefined || answer === null || answer === '') return true;
    
    // For multiple choice questions that can have custom answers
    if (question && (question.type === 'multiple' || question.type === 'multiple-image')) {
      if (Array.isArray(answer)) {
        // Filter out __custom__ markers and check if there's real content
        const realAnswers = answer.filter(a => a !== '__custom__');
        return realAnswers.length === 0;
      }
    }
    
    // For single choice with custom answer possibility
    if (answer === '__custom__') return true;
    
    return false;
  };
  
  const handleNext = () => {
    setAttemptedNext(true);
    
    if (!isCurrentPageComplete()) {
      return;
    }
    
    setAttemptedNext(false);
    
    // Check for page logic
    const { nextIndex: nextPageIndex, reason } = getNextPageIndex();
    
    // Check for disqualification
    if (nextPageIndex === -1) {
      // Submit answers with disqualified flag before showing disqualification screen
      handleSubmitDisqualified(reason);
      return;
    }
    
    if (nextPageIndex === null) {
      // No more pages: по ссылке интервьюера сначала доп. вопросы, иначе отправка
      if (hasExtra) {
        setOnExtraPage(true);
        setAttemptedExtra(false);
        window.scrollTo(0, 0);
      } else {
        handleSubmit();
      }
    } else {
      setPageHistory([...pageHistory, nextPageIndex]);
      setCurrentPageIndex(nextPageIndex);
      window.scrollTo(0, 0);
    }
  };
  
  const getNextPageIndex = () => {
    if (!survey || !survey.pages) return { nextIndex: null, reason: null };
    
    if (!survey.pageLogic || survey.pageLogic.length === 0) {
      // No logic, just go to next page
      const nextIndex = currentPageIndex + 1;
      return { nextIndex: nextIndex < survey.pages.length ? nextIndex : null, reason: null };
    }
    
    const currentPage = getCurrentPage();
    if (!currentPage) {
      const nextIndex = currentPageIndex + 1;
      return { nextIndex: nextIndex < survey.pages.length ? nextIndex : null, reason: null };
    }
    
    // Check if any logic rule applies
    for (const rule of survey.pageLogic) {
      // Check if the question is on the current page
      if (!currentPage.questionIds.includes(rule.questionId)) continue;
      
      const answer = answers[rule.questionId];
      if (!answer) continue;
      
      // Check if condition has answers array with items, otherwise use single answer
      const conditionAnswers = (Array.isArray(rule.condition?.answers) && rule.condition.answers.length > 0)
        ? rule.condition.answers
        : (rule.condition?.answer ? [rule.condition.answer] : []);
      
      if (!conditionAnswers || conditionAnswers.length === 0) continue;
      
      // Match answer with condition
      let matches = false;
      if (Array.isArray(answer)) {
        // Multiple choice: check if any selected answer matches
        matches = answer.some(ans => conditionAnswers.includes(ans));
      } else {
        // Single choice: check if answer matches
        matches = conditionAnswers.includes(String(answer));
      }
      
      if (matches) {
        // Check if action is to end survey (targetPageId === "END" or action === "end")
        if (rule.targetPageId === 'END' || rule.action === 'end') {
          // End survey immediately
          return { nextIndex: null, reason: null }; // This will trigger submit
        } else if (rule.action === 'endWithoutPayment') {
          // End survey without payment (disqualified)
          return { nextIndex: -1, reason: rule.disqualificationReason || null };
        } else if (rule.action === 'goToPage' && rule.targetPageId) {
          // Go to specific page
          const targetIndex = survey.pages.findIndex(p => p.id === rule.targetPageId);
          if (targetIndex >= 0) {
            return { nextIndex: targetIndex, reason: null };
          }
        }
      }
    }
    
    // Default: go to next page
    const nextIndex = currentPageIndex + 1;
    if (nextIndex < survey.pages.length) {
      return { nextIndex, reason: null };
    }
    
    return { nextIndex: null, reason: null }; // End of survey
  };
  
  const handleExtraSubmit = () => {
    setAttemptedExtra(true);
    if (!isExtraPageComplete()) return;
    handleSubmit();
  };

  const handleBack = () => {
    if (onExtraPage) {
      setOnExtraPage(false);
      setAttemptedExtra(false);
      window.scrollTo(0, 0);
      return;
    }
    if (pageHistory.length > 1) {
      const newHistory = [...pageHistory];
      newHistory.pop();
      const prevIndex = newHistory[newHistory.length - 1];
      setPageHistory(newHistory);
      setCurrentPageIndex(prevIndex);
      setAttemptedNext(false);
      window.scrollTo(0, 0);
    }
  };
  
  const handleSubmit = async () => {
    if (submitting) return;
    
    setSubmitting(true);
    setError('');
    
    try {
      // Upload all files first and get their URLs
      const answersWithFiles = { ...answers };
      
      for (const [questionId, file] of Object.entries(files)) {
        if (file) {
          const formData = new FormData();
          formData.append('file', file);
          
          try {
            const uploadResponse = await axios.post(`${API_BASE}/upload/public-survey-response`, formData, {
              headers: {
                'Content-Type': 'multipart/form-data'
              }
            });
            
            // Save file URL and metadata in answers
            answersWithFiles[questionId] = {
              fileUrl: uploadResponse.data.fileUrl,
              originalName: uploadResponse.data.originalName,
              size: uploadResponse.data.size,
              mimetype: uploadResponse.data.mimetype
            };
          } catch (uploadErr) {
            console.error('Error uploading file:', uploadErr);
            setError(`Ошибка загрузки файла: ${file.name}`);
            setSubmitting(false);
            return;
          }
        }
      }
      
      // Use the actual survey ID for submission (not the URL identifier which might be customSlug)
      const submitId = surveyId || id;
      await axios.post(
        `${API_BASE}/surveys/public/${submitId}/responses`,
        {
          answers: answersWithFiles,
          startedAt: new Date(startTime).toISOString(),
          ...(interviewerCode ? { interviewerId: interviewerCode } : {}),
          ...(hasExtra && onExtraPage ? { interviewerAnswers } : {})
        }
      );
      
      setCompleted(true);
      window.scrollTo(0, 0);
    } catch (err) {
      console.error('Error submitting public survey:', err);
      setError(err.response?.data?.error || 'Ошибка отправки ответов');
      setSubmitting(false);
    }
  };
  
  const handleSubmitDisqualified = async (reason) => {
    if (submitting) return;
    
    setSubmitting(true);
    
    try {
      // Upload all files first
      const answersWithFiles = { ...answers };
      
      for (const [questionId, file] of Object.entries(files)) {
        if (file) {
          const formData = new FormData();
          formData.append('file', file);
          
          try {
            const uploadResponse = await axios.post(`${API_BASE}/upload/public-survey-response`, formData, {
              headers: {
                'Content-Type': 'multipart/form-data'
              }
            });
            
            answersWithFiles[questionId] = {
              fileUrl: uploadResponse.data.fileUrl,
              originalName: uploadResponse.data.originalName,
              size: uploadResponse.data.size,
              mimetype: uploadResponse.data.mimetype
            };
          } catch (uploadErr) {
            console.error('Error uploading file:', uploadErr);
          }
        }
      }
      
      const submitId = surveyId || id;
      await axios.post(
        `${API_BASE}/surveys/public/${submitId}/responses`,
        { 
          answers: answersWithFiles,
          startedAt: new Date(startTime).toISOString(),
          ...(interviewerCode ? { interviewerId: interviewerCode } : {}),
          disqualified: true,
          disqualificationReason: reason || null
        }
      );
      
      setDisqualified(true);
      setDisqualificationReason(reason || '');
      window.scrollTo(0, 0);
    } catch (err) {
      console.error('Error submitting disqualified public survey:', err);
      // Still show disqualification screen even if submission fails
      setDisqualified(true);
      setDisqualificationReason(reason || '');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Calculate progress
  const getProgress = () => {
    if (!survey || !survey.pages) return 0;
    // прогресс = основные страницы + 1 (доп. вопросы интервьюера)
    const total = survey.pages.length + (hasExtra ? 1 : 0);
    return ((onExtraPage ? total : currentPageIndex + 1) / total) * 100;
  };
  
  const renderQuestion = (question) => {
    const answer = answers[question.id];
    const rangeMessage = selectionProgressMessage(question, answer) || matrixProgressMessage(question, answer);
    const hint = selectionHint(question);
    const showError = attemptedNext && (
      (question.required && isQuestionUnanswered(question.id)) || multiSelectBlocksProgress(question, answer) || matrixBlocksProgress(question, answer)
    );
    
    return (
      <Card key={question.id} sx={{ mb: 3, border: showError ? '2px solid red' : 'none' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {question.text}
            {question.required && <span style={{ color: 'red' }}> *</span>}
          </Typography>
          {hint && (
            <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1 }}>
              {hint}
            </Typography>
          )}
          
          {showError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {rangeMessage || 'На этот вопрос обязательно нужно ответить'}
            </Alert>
          )}
          
          {question.type === 'text' && (
            <TextField
              fullWidth
              multiline
              rows={3}
              value={answer || ''}
              onChange={(e) => handleAnswerChange(question.id, e.target.value)}
              placeholder="Введите ваш ответ"
            />
          )}
          
          {question.type === 'single' && (
            <>
              <RadioGroup
                value={answer === '__custom__' ? '__custom__' : (answer || '')}
                onChange={(e) => handleAnswerChange(question.id, e.target.value)}
              >
                {getShuffledOptions(question, surveyId || id).map((opt, idx) => (
                  <FormControlLabel key={idx} value={opt} control={<Radio />} label={opt} />
                ))}
                {question.allowCustom && (
                  <FormControlLabel value="__custom__" control={<Radio />} label="Другое (укажите)" />
                )}
              </RadioGroup>
              {question.allowCustom && answer === '__custom__' && (
                <TextField
                  fullWidth
                  value={typeof answer === 'string' && answer !== '__custom__' ? answer : ''}
                  onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                  placeholder="Укажите свой вариант"
                  sx={{ mt: 1 }}
                />
              )}
            </>
          )}
          
          {question.type === 'dropdown' && (
            <>
              <FormControl fullWidth>
                <InputLabel>Выберите вариант</InputLabel>
                <Select
                  value={answer === '__custom__' ? '__custom__' : (answer || '')}
                  label="Выберите вариант"
                  onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                >
                  {getShuffledOptions(question, surveyId || id).map((opt, idx) => (
                    <MenuItem key={idx} value={opt}>{opt}</MenuItem>
                  ))}
                  {question.allowCustom && (
                    <MenuItem value="__custom__">Другое (укажите)</MenuItem>
                  )}
                </Select>
              </FormControl>
              {question.allowCustom && answer === '__custom__' && (
                <TextField
                  fullWidth
                  value={typeof answer === 'string' && answer !== '__custom__' ? answer : ''}
                  onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                  placeholder="Укажите свой вариант"
                  sx={{ mt: 1 }}
                />
              )}
            </>
          )}
          
          {question.type === 'multiple' && (
            <>
              <FormGroup>
                {getShuffledOptions(question, surveyId || id).map((opt, idx) => {
                  const current = Array.isArray(answer) ? answer : [];
                  const optionChecked = current.includes(opt);
                  const atSelectionCap = isSelectionCapped(question, current);
                  return (
                  <FormControlLabel
                    key={idx}
                    control={
                      <Checkbox
                        checked={optionChecked}
                        disabled={!optionChecked && atSelectionCap}
                        onChange={(e) => {
                          if (e.target.checked && isSelectionCapped(question, current)) return;
                          if (e.target.checked) {
                            handleAnswerChange(question.id, [...current, opt]);
                          } else {
                            handleAnswerChange(question.id, current.filter(a => a !== opt));
                          }
                        }}
                      />
                    }
                    label={opt}
                  />
                  );
                })}
                {question.allowCustom && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Array.isArray(answer) && answer.includes('__custom__')}
                        disabled={!(Array.isArray(answer) && answer.includes('__custom__')) && isSelectionCapped(question, answer)}
                        onChange={(e) => {
                          const current = Array.isArray(answer) ? answer : [];
                          if (e.target.checked && isSelectionCapped(question, current)) return;
                          if (e.target.checked) {
                            handleAnswerChange(question.id, [...current, '__custom__']);
                          } else {
                            handleAnswerChange(question.id, current.filter(a => a !== '__custom__'));
                          }
                        }}
                      />
                    }
                    label="Другое (укажите)"
                  />
                )}
              </FormGroup>
              {question.allowCustom && Array.isArray(answer) && answer.includes('__custom__') && (
                <TextField
                  fullWidth
                  value={Array.isArray(answer) ? answer.find(a => a !== '__custom__' && !(question.options || []).includes(a)) || '' : ''}
                  onChange={(e) => {
                    const current = Array.isArray(answer) ? answer.filter(a => a === '__custom__' || (question.options || []).includes(a)) : ['__custom__'];
                    handleAnswerChange(question.id, [...current.filter(a => a !== '__custom__'), e.target.value || '__custom__']);
                  }}
                  placeholder="Укажите свой вариант"
                  sx={{ mt: 1 }}
                />
              )}
            </>
          )}
          
          {question.type === 'single-image' && (
            <>
              <RadioGroup
                value={answer === '__custom__' ? '__custom__' : (answer || '')}
                onChange={(e) => handleAnswerChange(question.id, e.target.value)}
              >
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  {getShuffledImageOptions(question, surveyId || id).map((opt, idx) => (
                    <Card
                      key={idx}
                      sx={{
                        width: 150,
                        cursor: 'pointer',
                        border: answer === opt.text ? '3px solid primary.main' : '1px solid grey.300'
                      }}
                      onClick={() => handleAnswerChange(question.id, opt.text)}
                    >
                      <img src={opt.url} alt={opt.text} style={{ width: '100%', height: 100, objectFit: 'cover' }} />
                      <CardContent>
                        <FormControlLabel value={opt.text} control={<Radio />} label={opt.text} />
                      </CardContent>
                    </Card>
                  ))}
                </Box>
                {question.allowCustom && (
                  <FormControlLabel value="__custom__" control={<Radio />} label="Другое (укажите)" />
                )}
              </RadioGroup>
              {question.allowCustom && answer === '__custom__' && (
                <TextField
                  fullWidth
                  value={typeof answer === 'string' && answer !== '__custom__' ? answer : ''}
                  onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                  placeholder="Укажите свой вариант"
                  sx={{ mt: 1 }}
                />
              )}
            </>
          )}
          
          {question.type === 'multiple-image' && (
            <>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {getShuffledImageOptions(question, surveyId || id).map((opt, idx) => {
                  const current = Array.isArray(answer) ? answer : [];
                  const imageSelected = current.includes(opt.text);
                  const imageBlocked = !imageSelected && isSelectionCapped(question, current);
                  return (
                  <Card
                    key={idx}
                    sx={{
                      width: 150,
                      cursor: imageBlocked ? 'not-allowed' : 'pointer',
                      opacity: imageBlocked ? 0.55 : 1,
                      border: imageSelected ? '3px solid primary.main' : '1px solid grey.300'
                    }}
                    onClick={() => {
                      if (imageBlocked) return;
                      if (imageSelected) {
                        handleAnswerChange(question.id, current.filter(a => a !== opt.text));
                      } else {
                        handleAnswerChange(question.id, [...current, opt.text]);
                      }
                    }}
                  >
                    <img src={opt.url} alt={opt.text} style={{ width: '100%', height: 100, objectFit: 'cover' }} />
                      <CardContent>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={imageSelected}
                              disabled={imageBlocked}
                            />
                          }
                          label={opt.text}
                        />
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
              {question.allowCustom && (
                <>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={Array.isArray(answer) && answer.includes('__custom__')}
                        disabled={!(Array.isArray(answer) && answer.includes('__custom__')) && isSelectionCapped(question, answer)}
                        onChange={(e) => {
                          const current = Array.isArray(answer) ? answer : [];
                          if (e.target.checked && isSelectionCapped(question, current)) return;
                          if (e.target.checked) {
                            handleAnswerChange(question.id, [...current, '__custom__']);
                          } else {
                            handleAnswerChange(question.id, current.filter(a => a !== '__custom__'));
                          }
                        }}
                      />
                    }
                    label="Другое (укажите)"
                    sx={{ mt: 2 }}
                  />
                  {Array.isArray(answer) && answer.includes('__custom__') && (
                    <TextField
                      fullWidth
                      value={Array.isArray(answer) ? answer.find(a => a !== '__custom__' && !(question.imageOptions || []).map(io => io.text).includes(a)) || '' : ''}
                      onChange={(e) => {
                        const imageTexts = (question.imageOptions || []).map(io => io.text);
                        const current = Array.isArray(answer) ? answer.filter(a => a === '__custom__' || imageTexts.includes(a)) : ['__custom__'];
                        handleAnswerChange(question.id, [...current.filter(a => a !== '__custom__'), e.target.value || '__custom__']);
                      }}
                      placeholder="Укажите свой вариант"
                      sx={{ mt: 1 }}
                    />
                  )}
                </>
              )}
            </>
          )}
          
          {question.type === 'matrix' && (() => {
            const mRows = question.matrixRows || [];
            const mCols = question.matrixColumns || question.matrixCols || [];
            if (mRows.length === 0 || mCols.length === 0) return null;
            const mAnswers = answer && typeof answer === 'object' && !Array.isArray(answer) ? answer : {};
            const missing = attemptedNext && matrixBlocksProgress(question, answer)
              ? new Set(getMissingMatrixRows(question, answer)) : new Set();
            return (
              <MatrixQuestion
                rows={mRows}
                columns={mCols}
                value={mAnswers}
                missingRows={missing}
                onChange={(next) => handleAnswerChange(question.id, next)}
              />
            );
          })()}

          {question.type === 'scale' && (() => {
            const sMin = question.scaleMin || 1;
            const sMax = question.scaleMax || 10;
            return (
              <Box sx={{ px: 1, mt: 3 }}>
                <Slider
                  value={answer !== undefined ? answer : sMin}
                  onChange={(e, val) => handleAnswerChange(question.id, val)}
                  min={sMin}
                  max={sMax}
                  step={1}
                  marks
                  valueLabelDisplay="on"
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                  <Typography variant="caption" color="text.secondary">{question.scaleMinLabel || sMin}</Typography>
                  <Typography variant="caption" color="text.secondary">{question.scaleMaxLabel || sMax}</Typography>
                </Box>
              </Box>
            );
          })()}

          {question.type === 'nps' && (
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
                {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                  <Button
                    key={num}
                    size="small"
                    variant={answer === num ? 'contained' : 'outlined'}
                    onClick={() => handleAnswerChange(question.id, num)}
                    sx={{ minWidth: 35, p: 0.5 }}
                  >
                    {num}
                  </Button>
                ))}
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="caption" color="text.secondary">Не рекомендую</Typography>
                <Typography variant="caption" color="text.secondary">Рекомендую</Typography>
              </Box>
            </Box>
          )}

          {question.type === 'likert' && (
            <RadioGroup value={answer || ''} onChange={(e) => handleAnswerChange(question.id, e.target.value)}>
              {(question.likertOptions || [
                'Полностью не согласен', 'Скорее не согласен', 'Нейтрально', 'Скорее согласен', 'Полностью согласен'
              ]).map((option, idx) => (
                <FormControlLabel key={idx} value={option} control={<Radio />} label={option} />
              ))}
            </RadioGroup>
          )}

          {question.type === 'rating' && (
            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              {[1, 2, 3, 4, 5].map(num => (
                <Button
                  key={num}
                  variant={answer === num ? 'contained' : 'outlined'}
                  onClick={() => handleAnswerChange(question.id, num)}
                >
                  {num}
                </Button>
              ))}
            </Box>
          )}
          
          {question.type === 'file' && (
            <Box>
              <input
                accept={question.fileTypes || '*/*'}
                style={{ display: 'none' }}
                id={`file-upload-${question.id}`}
                type="file"
                onChange={(e) => handleFileUpload(question.id, e.target.files[0])}
              />
              <label htmlFor={`file-upload-${question.id}`}>
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<UploadIcon />}
                >
                  Загрузить файл
                </Button>
              </label>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Максимальный размер файла: 15МБ
              </Typography>
              {files[question.id] && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Выбран файл: {files[question.id].name} ({(files[question.id].size / 1024 / 1024).toFixed(2)} МБ)
                </Typography>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
    );
  };
  
  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Typography>Загрузка опроса...</Typography>
        </Paper>
      </Container>
    );
  }
  
  if (!survey) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Paper sx={{ p: 3 }}>
          <Alert severity="error">{error || 'Опрос не найден'}</Alert>
        </Paper>
      </Container>
    );
  }
  
  if (completed) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h4" gutterBottom color="success.main">
            Спасибо за участие!
          </Typography>
          <Typography variant="body1" sx={{ mt: 2 }}>
            Ваши ответы успешно сохранены.
          </Typography>
        </Paper>
      </Container>
    );
  }
  
  // Show disqualification screen
  if (disqualified) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h4" gutterBottom color="warning.main">
            Благодарим за участие!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 2, mb: 2 }}>
            {disqualificationReason || 'Извините, исходя из Вашего ответа Вы не подходите для этого опроса.'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Пожалуйста, продолжайте проходить другие опросы на платформе!
          </Typography>
        </Paper>
      </Container>
    );
  }
  
  const currentPage = getCurrentPage();
  const pageQuestions = getCurrentPageQuestions();
  
  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          {survey.title}
        </Typography>
        
        {(survey.pages.length > 1 || hasExtra) && (
          <Box sx={{ my: 3 }}>
            <LinearProgress variant="determinate" value={getProgress()} />
            <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
              {Math.round(getProgress())}%
            </Typography>
          </Box>
        )}
        
        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
        
        {onExtraPage && (
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>Дополнительные вопросы</Typography>
            {extraQuestions.map((q) => {
              const a = interviewerAnswers[q.id];
              const showErr = attemptedExtra && q.required && isExtraUnanswered(q);
              const setA = (v) => setInterviewerAnswers((prev) => ({ ...prev, [q.id]: v }));
              return (
                <Card key={q.id} sx={{ mb: 3, border: showErr ? '2px solid red' : 'none' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {q.text}
                      {q.required && <span style={{ color: 'red' }}> *</span>}
                    </Typography>
                    {showErr && <Alert severity="error" sx={{ mb: 2 }}>На этот вопрос обязательно нужно ответить</Alert>}
                    {q.type === 'text' && (
                      <TextField
                        fullWidth
                        multiline={q.variant === 'long'}
                        rows={q.variant === 'long' ? 4 : undefined}
                        value={a || ''}
                        inputProps={{ maxLength: q.variant === 'long' ? 5000 : 500 }}
                        onChange={(e) => setA(e.target.value)}
                        placeholder="Введите ответ"
                      />
                    )}
                    {q.type === 'single' && (
                      <RadioGroup value={a || ''} onChange={(e) => setA(e.target.value)}>
                        {(q.options || []).map((opt) => (
                          <FormControlLabel key={opt} value={opt} control={<Radio />} label={opt} />
                        ))}
                      </RadioGroup>
                    )}
                    {q.type === 'multiple' && (
                      <FormGroup>
                        {(q.options || []).map((opt) => {
                          const cur = Array.isArray(a) ? a : [];
                          return (
                            <FormControlLabel
                              key={opt}
                              control={
                                <Checkbox
                                  checked={cur.includes(opt)}
                                  onChange={(e) => setA(e.target.checked ? [...cur, opt] : cur.filter((x) => x !== opt))}
                                />
                              }
                              label={opt}
                            />
                          );
                        })}
                      </FormGroup>
                    )}
                  </CardContent>
                </Card>
              );
            })}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button variant="outlined" onClick={handleBack} disabled={submitting}>Назад</Button>
              <Button variant="contained" onClick={handleExtraSubmit} disabled={submitting}>Отправить</Button>
            </Box>
          </Box>
        )}

        {currentPage && !onExtraPage && (
          <Box>
            {pageQuestions.map(q => renderQuestion(q))}
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
              <Button
                variant="outlined"
                onClick={handleBack}
                disabled={pageHistory.length <= 1 || submitting}
              >
                Назад
              </Button>
              
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={submitting}
              >
                {getNextPageIndex().nextIndex === null && !hasExtra ? 'Отправить' : 'Далее'}
              </Button>
            </Box>
          </Box>
        )}
      </Paper>
    </Container>
  );
}
